import { PAGE_SIZE } from '@/config/constants';
import { GET_ALL_FLYERS } from '@/graphql/queries';
import {
  districtVar,
  filterFacetsVar,
  flyerboardActiveFiltersVar,
  flyerboardAllFiltersVar,
  flyerboardFlyersAmountVar,
} from '@/graphql/state';
import {
  AllFlyerFilters,
  FlyerCardThumbnail,
  GetAllFlyersResponse,
} from '@/graphql/types';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useState, useEffect, useMemo } from 'react';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { useMetrics } from './useMetrics';
import { useAnalytics } from './useAnalytics';

interface FlyerboardPagination {
  trackId: string;
  flyerboardFilters: AllFlyerFilters;
  flyerType: string;
  audienceId?: string;
  districtId?: string | null;
  shouldUpdateFacets?: boolean;
  isPageVisible?: boolean;
}

export const useFlyerboardPagination = (props: FlyerboardPagination) => {
  const {
    trackId,
    flyerboardFilters,
    flyerType,
    audienceId,
    districtId: _districtId,
    shouldUpdateFacets = true,
    isPageVisible = true,
  } = props;

  const { track } = useAnalytics();
  const { trackImpressions } = useMetrics();
  const [flyers, setFlyers] = useState<FlyerCardThumbnail[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [isPaginationEnd, setIsPaginationEnd] = useState<boolean>(false);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [dataNotFound, setDataNotFound] = useState<boolean>(false);

  const district = useReactiveVar(districtVar);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const pages = useMemo(() => Math.ceil(total / PAGE_SIZE), [total]);
  const districtId = useMemo(
    () => Number(_districtId || district?.districtId),
    [district, _districtId],
  );

  useEffect(() => {
    if (districtId && isPageVisible) {
      getFlyers();
    }
  }, [page, triggerFetch, districtId]);

  const [getAllFlyers] = useLazyQuery<GetAllFlyersResponse>(GET_ALL_FLYERS);

  const getFlyers = async () => {
    if ((pages > 0 && page > pages) || !districtId) {
      return;
    }

    let filters: AllFlyerFilters = {
      ...flyerboardFilters,
      districtId: Number(districtId),
    };
    // This state is used to retrieve the facets on the FlyerboardFilters based on the current filters
    if (shouldUpdateFacets) {
      flyerboardAllFiltersVar(filters);
    }

    const { types, grades_age } = flyerboardActiveFilters;
    if (types.length > 0) {
      filters = {
        ...filters,
        categories: types,
      };
    }

    if (grades_age.length > 0) {
      filters = {
        ...filters,
        gradesOrAges: grades_age,
      };
    }

    try {
      setLoading(true);
      const { data } = await getAllFlyers({
        fetchPolicy: 'cache-and-network',
        variables: {
          input: {
            filters,
            pagination: {
              pageNumber: page,
              pageSize: PAGE_SIZE,
            },
          },
        },
      });

      const { items, pagination, facets } = data?.getAllFlyers || {
        items: [],
        pagination: {},
      };
      const paginationTotal = pagination?.total || 0;

      if (items && items?.length > 0) {
        trackImpressions({
          districtId: districtId.toString(),
          flyerType,
          audienceId,
          flyers: items,
        });
        setFlyers(prevData => [...prevData, ...items]);
        setTotal(paginationTotal);
        if (shouldUpdateFacets) {
          flyerboardFlyersAmountVar(paginationTotal);
        }
        if (paginationTotal === 0) {
          setDataNotFound(true);
        }
      } else {
        setIsPaginationEnd(true);
        setDataNotFound(true);
      }

      if (facets && !flyerboardFilters?.allDistrictSchools) {
        filterFacetsVar(facets);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const resetPagination = () => {
    setTotal(0);
    setFlyers([]);
    setDataNotFound(false);
    if (page !== 1) {
      setPage(1);
    } else {
      setTriggerFetch(prev => !prev);
    }
  };

  const favoriteFlyer = (flyerId: number) => {
    const index = flyers.findIndex(flyer => flyer.flyerId === flyerId);
    if (index >= 0) {
      const flyer = flyers[index];
      track({
        category: `${trackId}:${
          flyer?.isFavorite ? 'unfavorite' : 'favorite'
        }_flyer`,
        action: GenericTrackTypes.BUTTON_PRESSED,
        label: flyer?.title,
        value: flyer?.flyerId,
      });
      const newFlyers = [...flyers];
      newFlyers[index] = {
        ...newFlyers[index],
        isFavorite: !newFlyers[index].isFavorite,
      };
      setFlyers(newFlyers);
    }
  };

  return {
    resetPagination,
    loading,
    flyers,
    total,
    page,
    setPage,
    favoriteFlyer,
    pages,
    isPaginationEnd,
    dataNotFound,
  };
};
